import React, { useEffect, useState } from "react";
import { Cell, Grid, useObserver, useRouter } from "@shoothill/core";
import MaterialTable from "material-table";
import * as Defaults from "Globals/Defaults/TableOptions";
import { DarwinTablePageContent } from "Globals/Styles/AppStyling";
import { DarwinSelect } from "Components/AutoComplete/DarwinSelect";
import { RDDItemViewModel } from "./RDDItemViewModel";
import { RDDItemResponseViewModel } from "./RDDItemResponseViewModel";
import { AddEditResponseView } from "./Modals/Response/AddEditResponseView";
import { DesignDataItemView } from "./Modals/Items/DesignDataItemView";
import { DesignDataFileView } from "./Modals/Files/DesignDataFileView";
import { DesignDataStatusView } from "./Modals/Status/DesignDataStatusView";
import { styled } from "@shoothill/core";
import { CustomHr } from "../../CustomComponents";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { ControlLabel } from "Components/Form/ControlLabel";
import { Box, Checkbox, FormControlLabel, makeStyles, Typography } from "@material-ui/core";
import moment from "moment";
import { PrimaryButton } from "Components/Buttons/Buttons";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { pxToRem } from "Globals/Styles/AppTheme";
import { RDDViewModel } from "./RDDViewModel";
import { DeleteModal } from "Components/Modal/DeleteModal";
import { RDDFilterViewModel } from "./RDDFilterViewModel";
import { DarwinInput } from "Globals/Styles/Control/DarwinInput";
import { PatchedPagination } from "../../../../Components/Table/PatchedPagination";
import { TableListSearch } from "./../../../../Components/Table/TableListSearch";

// Styling
import EditLogo from "Content/AddEdit.svg";
import DeleteLogo from "Content/Bin.svg";
import RevisionLogo from "Content/Revision.svg";
import { DashedDivider, SolidDivider } from "Views/PurchaseOrder/Form/Views/Dividers";

interface IRDDTableProps {
    data: RDDItemViewModel[];
    isLoading: boolean;
    handleSearchChange: (val: string) => void;
    isDarwinGroupFilterValue: boolean | null;
    projectId: string;
    isDarwin: boolean | null;
    viewModel: RDDViewModel;
}

export const RDDTable: React.FunctionComponent<IRDDTableProps> = (props: IRDDTableProps) => {
    const { history } = useRouter();
    const viewModel = props.viewModel;
    const classes = useStyles();
    const [sortedState, setSortedState] = useState<RDDItemViewModel[]>(props.data);
    //useEffect below only gets run on initial render
    useEffect(() => {
        handleSortChange();
        return () => {};
    }, []);

    const [deleteOpen, setDeleteOpen] = React.useState(false);

    const setSearchString = (value: string) => {
        props.handleSearchChange(value);
    };

    const handleSortChange = (orderBy: number = 11, orderDirection: "asc" | "desc" = "asc") => {
        if (orderBy === 11) {
            if (orderDirection === "asc") {
                const sortedData = props.data.slice().sort((a: RDDItemViewModel, b: RDDItemViewModel) => {
                    const dateA = a.model.requestedDate ? new Date(a.model.requestedDate).getTime() : 0;
                    const dateB = b.model.requestedDate ? new Date(b.model.requestedDate).getTime() : 0;

                    return dateA - dateB;
                });
                setSortedState(sortedData);
            } else {
                const sortedData = props.data.slice().sort((a: RDDItemViewModel, b: RDDItemViewModel) => {
                    const dateA = a.model.requestedDate ? new Date(a.model.requestedDate).getTime() : 0;
                    const dateB = b.model.requestedDate ? new Date(b.model.requestedDate).getTime() : 0;

                    return dateB - dateA;
                });
                setSortedState(sortedData);
            }
        }
    };

    const renderPage = () => {
        return (
            <>
                <SearchBarContainer>
                    <TableListSearch placeholder={"Keyword search"} searchString={viewModel.getSearchString()} setSearchString={setSearchString} />
                    {props.isDarwin !== null && (
                        <PrimaryButton
                            displayName="Create new"
                            execute={() => viewModel.handleCreateNew(props.projectId, props.isDarwin)}
                            fullWidth={false}
                            canExecute={!props.isLoading}
                        />
                    )}
                    <PrimaryButton displayName="Import" fullWidth={false} canExecute={!props.isLoading} />
                    <PrimaryButton
                        displayName="Export all"
                        fullWidth={false}
                        canExecute={!props.isLoading}
                        execute={() => viewModel.exportAsCSV(props.projectId, props.isDarwin)}
                    />
                </SearchBarContainer>

                <Grid padding={"15px 0px 0px 0px"}>
                    <span className="small-title">Total late: {viewModel.getLateCount}</span>
                </Grid>
                <CustomHr margin={"6px 0"} padding={"0px 0px"} />
                <DarwinTablePageContent>
                    <MaterialTable
                        columns={[
                            {
                                title: "No. of responses",
                                field: "numberOfResponses",
                                render: (rowData: RDDItemViewModel) => viewModel.handleReturnResponseCount(rowData),
                            },
                            { title: "RDD sup ref.", field: "supplierReference" },
                            {
                                title: "Client ref.",
                                field: "clientReference",
                                render: (rowData: RDDItemViewModel) => (
                                    <>
                                        <ClientReference>{rowData.clientReferenceFormatted}</ClientReference>
                                    </>
                                ),
                            },
                            { title: "Supplier", field: "supplierName" },
                            {
                                title: "Item",
                                field: "numberOfItems",
                                align: "center",
                                render: (rowData: RDDItemViewModel) => (
                                    <>
                                        {rowData.numberOfItems > 0 ? (
                                            <PrimaryButton
                                                execute={() => viewModel.handleItemsShow(null, rowData.id, props.projectId, props.isDarwin)}
                                                displayName={rowData.numberOfItems.toString()}
                                                fullWidth={false}
                                                canExecute={!props.isLoading}
                                                stopPropagation={true}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </>
                                ),
                            },
                            {
                                title: "Request date",
                                field: "requestedDate",
                                render: (rowData: RDDItemViewModel) => rowData.requestedDateFormatted,
                            },
                            { title: "Requested by", field: "requestedByName" },
                            { title: "Discipline", field: "discipline" },
                            { title: "RequestedTo", field: "requestedTo" },
                            {
                                title: "Response required by",
                                field: "responseRequiredByDate",
                                render: (rowData: RDDItemViewModel) => rowData.responseRequiredByDateFormatted,
                            },
                            {
                                title: "Attchs. issued",
                                field: "numberOfFiles",
                                align: "center",
                                render: (rowData: RDDItemViewModel) => (
                                    <>
                                        {rowData.numberOfFiles > 0 ? (
                                            <PrimaryButton
                                                execute={() => viewModel.handleFilesShow(null, rowData.id, props.projectId, props.isDarwin)}
                                                displayName={rowData.numberOfFiles.toString()}
                                                fullWidth={false}
                                                canExecute={!props.isLoading}
                                                stopPropagation={true}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </>
                                ),
                            },
                            { title: "Priority", field: "priorityName" },
                            {
                                title: "Latest response",
                                field: "latestResponseName",
                                render: (rowData: RDDItemViewModel) => rowData.latestResponseName,
                            },
                            {
                                title: "Status",
                                defaultSort: "asc",
                                field: "statusName",
                                render: (rowData: RDDItemViewModel) => (
                                    <>
                                        <StatusBox $viewModel={viewModel} $id={rowData.model.designDataStatusId}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <DarwinSelect
                                                    displayName=""
                                                    stopPropagation={true}
                                                    executeWithEvent={(e, data: { id: string; displayName: string }) =>
                                                        viewModel.handleStatusId(e, rowData, data, props.projectId, props.isDarwin)
                                                    }
                                                    fullWidth={true}
                                                    getOptionLabel={(option: any) => option.displayName}
                                                    options={viewModel.statuses}
                                                    onBlur={() => rowData.isFieldValid("designDataStatusId")}
                                                    placeholder="Please select"
                                                    value={viewModel.handleReturnStatus(rowData)}
                                                    error={rowData.getError("designDataStatusId") !== ""}
                                                    validationMessage={rowData.getError("designDataStatusId")}
                                                    canExecute={true}
                                                />
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "right" }}>
                                                <span
                                                    onClick={(e) => viewModel.handleRowClick(e, rowData!, props.projectId)}
                                                    style={{ paddingTop: "20px", paddingRight: "20px" }}
                                                    className={classes.inputIcon}
                                                >
                                                    <img src={EditLogo} />
                                                </span>
                                                {/* <span onClick={(e) => viewModel.handleRevisionClick(e, rowData!, props.projectId)} className={classes.inputIcon} id="revision">
                                                    <img src={RevisionLogo} />
                                                </span> */}
                                            </div>
                                        </StatusBox>
                                    </>
                                ),
                            },
                        ]}
                        detailPanel={(rowData) => {
                            return (
                                <>
                                    <ResponsePanel>
                                        <div>
                                            <span>Response ({viewModel.handleReturnResponseCount(rowData)})</span>
                                        </div>
                                        {viewModel.designDataItemResponseViewModels
                                            .filter((d) => d.designDataId === rowData.id)
                                            .map((item: RDDItemResponseViewModel, index: number) => {
                                                return (
                                                    !item.model.isDeleted && (
                                                        <Box>
                                                            <Box>
                                                                <ResponseItemTextContainer>
                                                                    <Typography variant="body1">{item.model.comment}</Typography>
                                                                </ResponseItemTextContainer>
                                                            </Box>
                                                            <Box style={{ alignItems: "center" }}>
                                                                <ResponseItemTextContainer>
                                                                    <Typography variant="body1">
                                                                        {item.model.createdByName} on {item.model.receivedDateFormatted}
                                                                    </Typography>
                                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "right" }}>
                                                                        <span
                                                                            onClick={() => viewModel.handleAddResponseShow(item.id, rowData.id, props.projectId, props.isDarwin)}
                                                                            style={{ paddingRight: "20px" }}
                                                                            className={classes.inputIcon}
                                                                        >
                                                                            <img src={EditLogo} />
                                                                        </span>
                                                                        <span className={classes.inputIcon}>
                                                                            <img src={DeleteLogo} />
                                                                        </span>
                                                                    </div>
                                                                </ResponseItemTextContainer>
                                                            </Box>
                                                            <Box>
                                                                <SolidDivider gutterBottom={true} borderThickness={1} borderOpacity={1} />
                                                            </Box>
                                                        </Box>
                                                    )
                                                );
                                            })}
                                        <Box>
                                            <PrimaryButton
                                                execute={() => viewModel.handleAddResponseShow(null, rowData.id, props.projectId, props.isDarwin)}
                                                displayName="Add response"
                                                fullWidth={false}
                                                canExecute={!props.isLoading}
                                            />
                                        </Box>
                                    </ResponsePanel>
                                </>
                            );
                        }}
                        components={{
                            Pagination: PatchedPagination,
                        }}
                        onOrderChange={(orderBy, orderDirection) => handleSortChange(orderBy, orderDirection)}
                        options={Defaults.GetDarwinTableOptionsNoSearchV2()}
                        data={sortedState}
                        isLoading={props.isLoading}
                        onRowClick={(e, rowData) => {
                            viewModel.handleRowClick(e, rowData!, props.projectId);
                        }}
                        title=""
                    />
                </DarwinTablePageContent>
                {viewModel.addResponse && (
                    <AddEditResponseView
                        designDataId={viewModel.designDataId}
                        id={viewModel.designDataResponseId}
                        open={viewModel.addResponse}
                        onClose={() => viewModel.handleAddResponseClose(props.projectId, props.isDarwin)}
                        isdarwinrdd={viewModel.isDarwinGroup ? "1" : "0"}
                    />
                )}
                {viewModel.showItems && <DesignDataItemView designDataId={viewModel.designDataId} open={viewModel.showItems} onClose={() => viewModel.handleShowItemsClose()} />}
                {viewModel.showFiles && <DesignDataFileView designDataId={viewModel.designDataId} open={viewModel.showFiles} onClose={() => viewModel.handleShowFilesClose()} />}
                {viewModel.showStatusChange && (
                    <DesignDataStatusView
                        designDataId={viewModel.designDataId}
                        open={viewModel.showStatusChange}
                        onClose={() => viewModel.handleStatusChangeClose()}
                        onYes={() => viewModel.handleForwardToClientRDD(viewModel.designDataId!)}
                    />
                )}
            </>
        );
    };

    return useObserver(() => renderPage());
};

const StatusBox = styled(Box)<{ $viewModel: RDDViewModel; $id: string }>`
    .MuiFormControl-root {
        .MuiInputBase-root {
            background-color: ${(props) => props.$viewModel.setBackGroundStatus(props.$id)} !important;
            color: ${(props) => props.$viewModel.setColorStatus(props.$id)} !important;
        }
    }
`;

export const ResponsePanel: any = styled.li`
    position: relative;
    list-style-type: none;
    padding-left: 40px;
    padding-right: 40px;
    padding-top: 15px;
    margin-bottom: 10px;

    > div {
        font-size: 12px;
    }

    > div > span {
        font-weight: bold;
    }
`;

export const ResponseItemTextContainer = styled.div`
    margin-top: 10px;
    margin-bottom: 10px;
    padding-right: 0px;

    p {
        font-size: 12px !important;
    }
`;

export const ClientReference = styled.span`
    text-decoration: underline !important;
    color: blue !important;
`;

export const SearchBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 20px 0px 10px;

    > div:nth-child(1) {
        max-width: 50%;
        width: 100%;
    }

    > button {
        width: auto;
        white-space: nowrap;
        min-width: 165px;
        margin-left: 30px;

        span {
            font-size: ${pxToRem(12)} !important;
        }
    }
`;

export const SubTitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0px 30px 10px;

    > p {
        font-size: 18px;
        letter-spacing: -0.36px;
        font-weight: 600;
    }

    > div {
        button {
            margin-bottom: 8px;
        }

        p {
            font-size: 8px;
            font-weight: 600;
            line-height: 11px;
            letter-spacing: -0.16px;
        }
    }
`;

const useStyles = makeStyles({
    buttonGroup: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    autoCompleteInput: {
        display: "flex",
        justifyContent: "start",
        paddingRight: "30px !important",
        paddingLeft: "40px !important",
        "& div.MuiAutocomplete-inputRoot": {
            width: "90%",
            height: "90%",
            display: "flex",
            alignItems: "center",
        },
    },
    input: {
        "& .MuiInputBase-root": {
            display: "flex !important",
            alignItems: "center !important",
            justifyContent: "center !important",
            margin: "0 5px",
            height: "90%",
            backgroundColor: "gold",
            width: "90%",

            "& > input": {
                textAlign: "right",
                fontSize: "12px",
            },
        },
    },
    inputIcon: {
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "20px",
    },
});
